import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';

import { ReactComponent as IconLoader } from '../../assets/icon/ic_loading.svg';

import styles from './Table.module.scss';

const MOBILE_VISIBLE_FIELD_COUNT = 4;

const Table = ({
  children, loading, mobileCards, mobileVisibleFields
}) => {
  const tableRef = useRef(null);
  const {
    application: { isCoOrderHeaderFixed },
  } = useSelector((state) => ({
    application: state?.application,
  }), shallowEqual);

  useLayoutEffect(() => {
    const table = tableRef.current;

    if (!table || !mobileCards) return undefined;

    const headers = Array.from(table.querySelectorAll('thead tr:last-child th'))
      .map((header) => header.textContent.trim());
    const activeMobileFields = headers.filter((header) => (
      mobileVisibleFields.some((label) => header.toLowerCase() === label.toLowerCase())
    ));

    table.querySelectorAll('tbody tr:not([data-detail-row="true"])').forEach((row) => {
      let columnIndex = 0;
      let fieldIndex = 0;
      let secondaryFieldCount = 0;

      row.querySelectorAll(':scope > td').forEach((cell) => {
        if (cell.dataset.mobileControl !== undefined) return;

        cell.removeAttribute('data-mobile-action');
        cell.removeAttribute('data-mobile-empty-action');
        cell.removeAttribute('data-mobile-field-index');
        cell.removeAttribute('data-mobile-primary');
        cell.removeAttribute('data-mobile-secondary');
        cell.removeAttribute('data-mobile-order');

        if (cell.colSpan > 1) {
          cell.setAttribute('data-mobile-empty-state', 'true');
          columnIndex += cell.colSpan;
          return;
        }

        cell.removeAttribute('data-mobile-empty-state');
        const label = headers[columnIndex] || '';
        const isActionColumn = !label || /^actions?$/i.test(label);
        const configuredFieldIndex = activeMobileFields.findIndex((configuredLabel) => (
          configuredLabel.toLowerCase() === label.toLowerCase()
        ));
        cell.setAttribute('data-mobile-label', label);

        if (!isActionColumn) {
          const hasCustomMobileFields = activeMobileFields.length > 0;
          const mobileFieldIndex = hasCustomMobileFields ? configuredFieldIndex : fieldIndex;
          const isSecondary = hasCustomMobileFields
            ? configuredFieldIndex === -1
            : fieldIndex >= MOBILE_VISIBLE_FIELD_COUNT;

          cell.setAttribute('data-mobile-field-index', `${mobileFieldIndex}`);
          if (mobileFieldIndex >= 0) cell.setAttribute('data-mobile-order', `${mobileFieldIndex}`);
          if (mobileFieldIndex === 0) cell.setAttribute('data-mobile-primary', 'true');
          if (isSecondary) {
            cell.setAttribute('data-mobile-secondary', 'true');
            secondaryFieldCount += 1;
          }
          fieldIndex += 1;
        } else {
          cell.setAttribute('data-mobile-action', 'true');
          if (!cell.textContent.trim() && cell.childElementCount === 0) {
            cell.setAttribute('data-mobile-empty-action', 'true');
          }
        }

        columnIndex += 1;
      });

      if (secondaryFieldCount > 0) {
        row.setAttribute('data-mobile-has-details', 'true');
      } else {
        row.removeAttribute('data-mobile-has-details');
      }
    });

    return undefined;
  }, [children, mobileCards, mobileVisibleFields]);

  return (
    <div
      className={`${styles.tableContainer} ${mobileCards ? styles.mobileCards : ''}
      relative px-[10px] overflow-auto customScroll
      ${isCoOrderHeaderFixed ? 'max-h-[calc(100vh-150px-95px-70px-85px)]' : ''}`}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/40 z-[3]">
          <div>
            <IconLoader widht={100} height={50} className="w-[30px] h-[30px] text-dark dark:text-secondary spin" />
          </div>
        </div>
      )}
      <table ref={tableRef} className={`${styles.table} w-full`}>{children}</table>
    </div>
  );
};

Table.propTypes = {
  children: PropTypes.node,
  loading: PropTypes.bool,
  mobileCards: PropTypes.bool,
  mobileVisibleFields: PropTypes.arrayOf(PropTypes.string)
};

Table.defaultProps = {
  children: undefined,
  loading: false,
  mobileCards: true,
  mobileVisibleFields: []
};

export default Table;
